import { Component, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatTabChangeEvent } from '@angular/material/tabs'
import {
  ApiService,
  AppointmentDataService,
  AppointmentModel,
  ClinicService,
  Eligibility,
  findInsuranceForAccount,
  Patient,
  PatientDataService,
  PatientInsurance,
  PatientInsuranceStatus,
  PatientInsuranceType,
} from '@mmx/shared'
import { of, Subject } from 'rxjs'
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators'

import { PullEligibilityDialogComponent, PullEligibilityDialogInput } from '../pull-eligibility-dialog/pull-eligibility-dialog.component'

@Component({
  selector: 'mmx-appointment-eligibility-card',
  templateUrl: './appointment-eligibility-card.component.html',
  styleUrls: ['./appointment-eligibility-card.component.scss'],
  standalone: false,
})
export class AppointmentEligibilityCardComponent implements OnInit, OnDestroy {
  insuranceType = PatientInsuranceType

  private tab = PatientInsuranceType.PRIMARY
  private patient: Patient
  private appointment: AppointmentModel
  private allInsurances: PatientInsurance[]
  private activeInsurances: PatientInsurance[]
  private onDestroy$ = new Subject<void>()

  constructor(
    public data: AppointmentDataService,
    private patientDataService: PatientDataService,
    private api: ApiService,
    private dialog: MatDialog,
    private clinicService: ClinicService,
  ) {}

  facility$ = this.data.facility$

  patient$ = this.patientDataService.patient$.pipe(
    takeUntil(this.onDestroy$),
    tap((patient) => (this.patient = patient)),
  )

  appointment$ = this.data.appointment$.pipe(
    takeUntil(this.onDestroy$),
    tap((appointment) => (this.appointment = appointment)),
  )

  serviceCodes$ = this.data.serviceCodes$
  placeOfService$ = this.appointment$.pipe(
    map((appointment) => appointment?.location?.placeOfServiceTypes),
  )

  insurances$ = this.patientDataService.insurances$.pipe(
    takeUntil(this.onDestroy$),
    map((insurances) => [
      insurances,
      // filter out inactive policies
      insurances.filter(
        ({ status }) => status === PatientInsuranceStatus.Active,
      ),
    ]),
    // only emit when we have an active insurance policy
    tap(([allInsurances, activeInsurances]) => {
      this.allInsurances = allInsurances
      this.activeInsurances = activeInsurances
    }),
  )

  ngOnInit(): void {
    this.patient$.subscribe()
    this.insurances$.subscribe()
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  primaryEligibilityId$ = this.appointment$.pipe(
    takeUntil(this.onDestroy$),
    map(
      (appointment) => appointment.eligibilityDetails.primary?.id,
    ),
  )

  primaryEligibility$ = this.primaryEligibilityId$.pipe(
    switchMap((eligibilityId) =>
      eligibilityId ? this.api.getEligibility(eligibilityId) : of(null),
    ),
  )

  secondaryEligibilityId$ = this.appointment$.pipe(
    takeUntil(this.onDestroy$),
    map(
      (appointment) =>
        appointment.eligibilityDetails.secondary?.id,
    ),
  )

  secondaryEligibility$ = this.secondaryEligibilityId$.pipe(
    switchMap((eligibilityId) =>
      eligibilityId ? this.api.getEligibility(eligibilityId) : of(null),
    ),
  )

  tertiaryEligibilityId$ = this.appointment$.pipe(
    takeUntil(this.onDestroy$),
    map(
      (appointment) => appointment.eligibilityDetails.tertiary?.id,
    ),
  )

  tertiaryEligibility$ = this.tertiaryEligibilityId$.pipe(
    switchMap((eligibilityId) =>
      eligibilityId ? this.api.getEligibility(eligibilityId) : of(null),
    ),
  )

  getPatientInsurance(
    type: PatientInsuranceType,
    { includeInactive = false } = {},
  ): PatientInsurance | undefined {
    const accountId = this.appointment?.account?.id
    const candidates = includeInactive
      ? this.allInsurances
      : this.allInsurances?.filter((insurance) => insurance.status !== PatientInsuranceStatus.Inactive)

    // so the preselected payer is visit-correct, not just the record's raw type
    return findInsuranceForAccount(candidates ?? [], type, this.clinicService.clinic, accountId, { alwaysScopeToAccount: true })
  }

  getShield(eligibility: Eligibility): string {
    if (eligibility.active && eligibility.valid) {
      return 'gpp_good'
    } else if (eligibility.valid) {
      return 'gpp_bad'
    } else {
      return 'shield'
    }
  }

  updateEstimation(): void {
    this.patientDataService.insurancesChanged$.next()
  }

  protected pullEligibilityData(
    type: PatientInsuranceType,
  ): PullEligibilityDialogInput {
    const data: PullEligibilityDialogInput = {
      patient: this.patient,
      appointment: this.appointment,
      insurances: this.activeInsurances,
      insurance: this.getPatientInsurance(type, { includeInactive: true }),
      insuranceType: type,
    }

    return data
  }

  onTabChange(event: MatTabChangeEvent) {
    switch (event.index) {
      case 0:
        this.tab = PatientInsuranceType.PRIMARY
        break
      case 1:
        this.tab = PatientInsuranceType.SECONDARY
        break
      case 2:
        this.tab = PatientInsuranceType.TERTIARY
        break
    }
  }

  openPullEligibilityDialog() {
    const type = this.tab
    const data = this.pullEligibilityData(type)
    const dialogRef = this.dialog.open(PullEligibilityDialogComponent, {
      panelClass: 'dialog-form',
      width: '500px',
      data,
    })
    dialogRef.afterClosed().pipe(
      takeUntil(this.onDestroy$),
      filter(Boolean),
    ).subscribe((eligibility: Eligibility) => {
      if (eligibility) {
        this.patientDataService.insurancesChanged$.next()
        this.data.appointmentUpdated$.next()
      }
    })
  }
}
